import React, { useState, useEffect, useCallback, Suspense, lazy, useRef } from "react";
import {
  Layout, Menu, Button, Space, Typography, message, Badge,
  Input, List, Tag, Spin, Popover, ConfigProvider, Divider,
} from "antd";
import {
  DashboardOutlined,
  FolderOpenOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  UserOutlined,
  SwapOutlined,
  UnorderedListOutlined,
  SettingOutlined,
  BellOutlined,
  SearchOutlined,
  BarChartOutlined,
  TeamOutlined,
  AlertOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  ShoppingCartOutlined,
  InboxOutlined,
  CarOutlined,
  MobileOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

// ── Eager imports (tiny, always needed) ───────────────────────────────────────
import LoginModal  from "./components/LoginModal";
import RequireRole from "./components/RequireRole";
import api         from "./api";
import { hasPerm } from "./permissions";


// ── Lazy imports (each page loads only when the user navigates to it) ─────────
// This splits the 3 MB bundle into ~20 small chunks.
// The browser only downloads a chunk when the user first visits that route.
const Dashboard            = lazy(() => import("./components/Dashboard"));
const InventoryLedger      = lazy(() => import("./pages/InventoryLedger"));
const SalesOrders          = lazy(() => import("./pages/SalesOrders"));
const PendingOrders        = lazy(() => import("./pages/PendingOrders"));
const SalesOutRecords      = lazy(() => import("./pages/SalesOutRecords"));
const MobileOutboundConfirm = lazy(() => import("./pages/MobileOutboundConfirm"));
// Management (Operations Centre) removed — no longer in use
const AssemblySchedule     = lazy(() => import("./pages/AssemblySchedule"));
const Admin                = lazy(() => import("./pages/Admin"));
const InventoryAnalytics   = lazy(() => import("./pages/InventoryAnalytics"));
const InventoryLookup      = lazy(() => import("./pages/InventoryLookup"));
const CustomerOrders       = lazy(() => import("./pages/CustomerOrders"));
const DeliveryNote         = lazy(() => import("./pages/DeliveryNote"));
const MobileScan           = lazy(() => import("./pages/MobileScan"));
const PickingSession       = lazy(() => import("./pages/PickingSession"));
const MobileQuickPull      = lazy(() => import("./pages/MobileQuickPull"));
const MobilePickingHistory = lazy(() => import("./pages/MobilePickingHistory"));
// AssemblyBoard removed — no longer in use
const PurchaseOrders        = lazy(() => import("./pages/PurchaseOrders"));
const Receiving             = lazy(() => import("./pages/Receiving"));
const TaskSheetPreview     = lazy(() => import("./pages/TaskSheetPreview"));
const LocationManagement   = lazy(() => import("./pages/LocationManagement"));
const PickingHistoryPage   = lazy(() => import("./pages/PickingHistoryPage"));
const PickingDrafts        = lazy(() => import("./pages/PickingDrafts"));
const PickingDraftImport   = lazy(() => import("./pages/PickingDraftImport"));
const DeliveryPlanner      = lazy(() => import("./pages/DeliveryPlanner"));
const SkuMaster            = lazy(() => import("./pages/SkuMaster"));
const WarehouseMap         = lazy(() => import("./pages/WarehouseMap"));
// ProductHierarchy deprecated — merged into SkuMaster tree (keep backend routes intact)
const Records              = lazy(() => import("./pages/Records"));
const Shortages            = lazy(() => import("./pages/Shortages"));

// ── Shared page-level loading fallback ────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      minHeight: 320,
    }}>
      <Spin size="large" />
    </div>
  );
}

const { Header, Content } = Layout;
const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
//  GLOBAL SEARCH BAR
// ─────────────────────────────────────────────────────────────────────────────
function GlobalSearch() {
  const [query, setQuery]   = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]     = useState(false);
  const navigate            = useNavigate();

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setResults(res.data);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), 350);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  const gotoOrder = (invoice) => {
    setOpen(false);
    setQuery("");
    navigate(`/delivery-note/${invoice}`);
  };

  const content = (
    <div style={{ width: 360, maxHeight: 400, overflowY: "auto" }}>
      {loading && <div style={{ textAlign: "center", padding: 16 }}><Spin size="small" /></div>}
      {!loading && results && (
        <>
          {results.orders?.length > 0 && (
            <>
              <Text type="secondary" style={{ fontSize: 11, padding: "0 4px" }}>ORDERS</Text>
              <List
                size="small"
                dataSource={results.orders}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: "pointer", padding: "6px 4px" }}
                    onClick={() => gotoOrder(item.invoice_no)}
                  >
                    <Space>
                      <Text code style={{ fontSize: 12 }}>{item.invoice_no}</Text>
                      <Text style={{ fontSize: 12 }}>{item.customer}</Text>
                      <Tag
                        color={item.status === "Completed" ? "success" : "processing"}
                        style={{ fontSize: 10 }}
                      >
                        {item.status || "Pending"}
                      </Tag>
                    </Space>
                  </List.Item>
                )}
              />
            </>
          )}
          {results.inventory?.length > 0 && (
            <>
              <Divider style={{ margin: "6px 0" }} />
              <Text type="secondary" style={{ fontSize: 11, padding: "0 4px" }}>INVENTORY</Text>
              <List
                size="small"
                dataSource={results.inventory}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: "pointer", padding: "6px 4px" }}
                    onClick={() => { setOpen(false); navigate("/analytics"); }}
                  >
                    <Space>
                      <Text style={{ fontSize: 12 }}>{item.type_code}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>/ {item.color_code}</Text>
                      <Tag
                        color={item.quantity === 0 ? "error" : item.quantity < 5 ? "volcano" : item.quantity < 10 ? "gold" : "success"}
                        style={{ fontSize: 10 }}
                      >
                        Qty: {item.quantity}
                      </Tag>
                    </Space>
                  </List.Item>
                )}
              />
            </>
          )}
          {results.purchase_orders?.length > 0 && (
            <>
              <Divider style={{ margin: "6px 0" }} />
              <Text type="secondary" style={{ fontSize: 11, padding: "0 4px" }}>PURCHASE ORDERS</Text>
              <List
                size="small"
                dataSource={results.purchase_orders}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: "pointer", padding: "6px 4px" }}
                    onClick={() => { setOpen(false); navigate("/inventory/purchase-orders"); }}
                  >
                    <Space>
                      <Text code style={{ fontSize: 12 }}>{item.po_no}</Text>
                      <Text style={{ fontSize: 12 }}>{item.supplier}</Text>
                      <Tag color={item.status === "Received" ? "success" : item.status === "Ordered" ? "processing" : "default"}
                        style={{ fontSize: 10 }}>{item.status}</Tag>
                    </Space>
                  </List.Item>
                )}
              />
            </>
          )}
          {results.orders?.length === 0 && results.inventory?.length === 0 && !results.purchase_orders?.length && (
            <div style={{ padding: 16, textAlign: "center", color: "#bbb" }}>No results found</div>
          )}
        </>
      )}
      {!loading && !results && query.length >= 2 && (
        <div style={{ padding: 16, textAlign: "center", color: "#bbb" }}>Type to search…</div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open && query.length >= 2}
      onOpenChange={(v) => { if (!v) setOpen(false); }}
      placement="bottomRight"
    >
      <Input
        prefix={<SearchOutlined style={{ color: "#aaa" }} />}
        placeholder="Search invoice / customer / product…"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={{ width: 280, borderRadius: 20, fontSize: 13 }}
        allowClear
      />
    </Popover>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  NOTIFICATION BELL
// ─────────────────────────────────────────────────────────────────────────────
function NotificationBell() {
  const [data, setData]   = useState(null);
  const [open, setOpen]   = useState(false);
  const navigate          = useNavigate();

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchNotifs]);

  const total = data?.total || 0;

  const notifContent = (
    <div style={{ width: 320, maxHeight: 400, overflowY: "auto" }}>
      {!data ? (
        <div style={{ padding: 16, textAlign: "center" }}><Spin size="small" /></div>
      ) : (
        <>
          {data.overdue_assembly?.length > 0 && (
            <>
              <div style={{ padding: "8px 12px", background: "#fff1f0", borderBottom: "1px solid #ffccc7" }}>
                <Space>
                  <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
                  <Text strong style={{ fontSize: 13, color: "#ff4d4f" }}>
                    Overdue Assembly ({data.overdue_assembly.length})
                  </Text>
                </Space>
              </div>
              <List
                size="small"
                dataSource={data.overdue_assembly}
                renderItem={(item) => (
                  <List.Item
                    style={{ padding: "6px 12px", cursor: "pointer" }}
                    onClick={() => { setOpen(false); navigate("/assembly"); }}
                  >
                    <Space>
                      <Text code style={{ fontSize: 11 }}>{item.invoice_no}</Text>
                      <Text style={{ fontSize: 12 }}>{item.customer}</Text>
                      <Tag color="error" style={{ fontSize: 10 }}>
                        Due {item.assemble_date ? new Date(item.assemble_date).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles" }) : "—"}
                      </Tag>
                    </Space>
                  </List.Item>
                )}
              />
            </>
          )}
          {data.low_stock?.length > 0 && (
            <>
              <div style={{ padding: "8px 12px", background: "#fff7e6", borderBottom: "1px solid #ffd591" }}>
                <Space>
                  <AlertOutlined style={{ color: "#fa8c16" }} />
                  <Text strong style={{ fontSize: 13, color: "#fa8c16" }}>
                    Critical Low Stock ({data.low_stock.length})
                  </Text>
                </Space>
              </div>
              <List
                size="small"
                dataSource={data.low_stock}
                renderItem={(item) => (
                  <List.Item
                    style={{ padding: "6px 12px", cursor: "pointer" }}
                    onClick={() => { setOpen(false); navigate("/analytics"); }}
                  >
                    <Space>
                      <Text style={{ fontSize: 12 }}>{item.type_code}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>/ {item.color_code}</Text>
                      <Tag color={item.quantity === 0 ? "error" : "volcano"} style={{ fontSize: 10 }}>
                        Qty: {item.quantity}
                      </Tag>
                    </Space>
                  </List.Item>
                )}
              />
            </>
          )}
          {data.po_overdue?.length > 0 && (
            <>
              <div style={{ padding: "8px 12px", background: "#fff2f0", borderBottom: "1px solid #ffccc7" }}>
                <Space>
                  <CarOutlined style={{ color: "#ff4d4f" }} />
                  <Text strong style={{ fontSize: 13, color: "#ff4d4f" }}>
                    Overdue PO ({data.po_overdue.length})
                  </Text>
                </Space>
              </div>
              <List
                size="small"
                dataSource={data.po_overdue}
                renderItem={(item) => (
                  <List.Item
                    style={{ padding: "6px 12px", cursor: "pointer" }}
                    onClick={() => { setOpen(false); navigate("/inventory/purchase-orders"); }}
                  >
                    <Space>
                      <Text code style={{ fontSize: 11 }}>{item.po_no}</Text>
                      <Text style={{ fontSize: 12 }}>{item.supplier}</Text>
                      <Tag color="error" style={{ fontSize: 10 }}>
                        ETA {item.expected_date ? new Date(item.expected_date).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles" }) : "—"}
                      </Tag>
                    </Space>
                  </List.Item>
                )}
              />
            </>
          )}
          {total === 0 && (
            <div style={{ padding: "24px 16px", textAlign: "center", color: "#52c41a" }}>
              <CheckCircleOutlined style={{ fontSize: 20, marginBottom: 8 }} />
              <br />
              <Text style={{ color: "#52c41a" }}>All clear! No alerts.</Text>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <Popover
      content={notifContent}
      title={
        <Space style={{ padding: "4px 0" }}>
          <BellOutlined />
          <Text strong>Notifications</Text>
          <Button size="small" type="link" onClick={fetchNotifs} style={{ padding: 0, marginLeft: 4 }}>
            Refresh
          </Button>
        </Space>
      }
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Badge count={total} offset={[-2, 2]}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          style={{ color: total > 0 ? "#ff4d4f" : "#888" }}
        />
      </Badge>
    </Popover>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SIDEBAR
// ─────────────────────────────────────────────────────────────────────────────
function AppSider({ role, user, mobileOpen, onMobileClose, isMobile }) {
  const can = (perm) => hasPerm(user, perm);
  const isAdmin = role === "admin";
  const location = useLocation();

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === "/")                    return "dashboard";
    if (path.startsWith("/inventory/purchase-orders")) return "purchase-orders";
    if (path.startsWith("/inventory/receiving"))           return "receiving";
    if (path.startsWith("/inventory/ledger"))          return "inv-ledger";
    if (path.startsWith("/inventory/lookup"))          return "inv-lookup";
    if (path.startsWith("/inventory/sku-master"))          return "sku-master";
    if (path === "/analytics")               return "analytics";
    if (path === "/sales/orders")        return "sales-orders";
    if (path === "/sales/pending")       return "sales-pending";
    // sales/out hidden from menu — covered by Records Center
    if (path === "/customers")           return "customers";
    if (path === "/assembly")            return "assembly";
    // assembly-board and management routes removed from menu
    if (path.startsWith("/delivery-note")) return "delivery-note";
    if (path === "/admin")               return "admin";
    if (path.startsWith("/warehouse/mobile")) return "mobile-hub";
    if (path === "/records")               return "records";
    if (path.startsWith("/delivery/planner")) return "delivery-planner";
    if (path.startsWith("/picking/drafts")) return "picking-drafts"; // sidebar item under Warehouse
    if (path.startsWith("/picking/import")) return "picking-import";
    if (path.startsWith("/shortages"))     return "shortages";
    if (path.startsWith("/warehouse/map")) return "warehouse-map";
    return "dashboard";
  };

  return (
    <>
      {isMobile && mobileOpen && (
        <div
          onClick={onMobileClose}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 199,
          }}
        />
      )}

      <div
        style={{
          position: "fixed",
          top: 0, left: 0,
          width: isMobile ? 260 : 200,
          height: "100vh",
          background: "#0f1f32",
          boxShadow: "2px 0 8px rgba(0,0,0,0.18)",
          zIndex: 200,
          display: "flex",
          flexDirection: "column",
          transform: (isMobile && !mobileOpen) ? "translateX(-100%)" : "translateX(0)",
          transition: isMobile ? "transform 0.25s cubic-bezier(.4,0,.2,1)" : "none",
          touchAction: "pan-y",
        }}
        onTouchMove={e => e.stopPropagation()}
        onWheel={e => e.stopPropagation()}
      >
        {/* Logo */}
        <div style={{
          color: "#fff",
          padding: "16px 20px",
          fontWeight: 700,
          fontSize: 16,
          letterSpacing: 1,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, background: "rgba(255,255,255,0.15)", borderRadius: 4, padding: "2px 7px", fontWeight: 800, letterSpacing: 2, fontFamily: "monospace" }}>WMS</span>
            <span style={{ fontSize: 13, fontWeight: 400, opacity: 0.7, letterSpacing: 0 }}>Warehouse</span>
          </span>
          {isMobile && (
            <span
              onClick={onMobileClose}
              style={{ color: "rgba(255,255,255,0.55)", fontSize: 22, cursor: "pointer", lineHeight: 1, padding: "0 4px", userSelect: "none" }}
            >
              ✕
            </span>
          )}
        </div>

        {/* Scrollable menu */}
        <div style={{
          flex: 1,
          overflowY: "scroll",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          paddingBottom: "env(safe-area-inset-bottom, 24px)",
        }}>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[getSelectedKey()]}
            defaultOpenKeys={[]}
            onClick={isMobile ? onMobileClose : undefined}
            style={{ background: "transparent", border: "none" }}
          >
            <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
              <Link to="/">Dashboard</Link>
            </Menu.Item>

            {(can("stock_lookup") ||
              can("analytics") || can("purchase_orders") || can("receiving") ||
              can("sku_master")) && (
              <Menu.SubMenu key="inventory" icon={<FolderOpenOutlined />} title="Inventory">
                {/* Inventory Ledger hidden — covered by Records Center */}
                {can("stock_lookup") && (
                  <Menu.Item key="inv-lookup" icon={<SearchOutlined style={{ color: "#1677ff" }} />}>
                    <Link to="/inventory/lookup">Stock Lookup</Link>
                  </Menu.Item>
                )}
                {can("analytics") && (
                  <Menu.Item key="analytics" icon={<BarChartOutlined style={{ color: "#1677ff" }} />}>
                    <Link to="/analytics">Analytics</Link>
                  </Menu.Item>
                )}
                {can("purchase_orders") && (
                  <Menu.Item key="purchase-orders" icon={<ShoppingCartOutlined style={{ color: "#1677ff" }} />}>
                    <Link to="/inventory/purchase-orders">Purchase Orders</Link>
                  </Menu.Item>
                )}
                {can("receiving") && (
                  <Menu.Item key="receiving" icon={<InboxOutlined style={{ color: "#52c41a" }} />}>
                    <Link to="/inventory/receiving">Receiving</Link>
                  </Menu.Item>
                )}
                {can("sku_master") && (
                  <Menu.Item key="sku-master" icon={<AppstoreOutlined style={{ color: "#6d28d9" }} />}>
                    <Link to="/inventory/sku-master">SKU Master</Link>
                  </Menu.Item>
                )}

              </Menu.SubMenu>
            )}

            {(can("sales_orders") || can("pending_orders") || can("customers")) && (
              <Menu.SubMenu key="sales" icon={<UnorderedListOutlined />} title="Sales & Orders">
                {can("sales_orders") && (
                  <Menu.Item key="sales-orders">
                    <Link to="/sales/orders">Sales Orders</Link>
                  </Menu.Item>
                )}
                {can("pending_orders") && (
                  <Menu.Item key="sales-pending" icon={<ClockCircleOutlined />}>
                    <Link to="/sales/pending">Pending Orders</Link>
                  </Menu.Item>
                )}
                {/* Out Records hidden — covered by Records Center */}
                {can("customers") && (
                  <Menu.Item key="customers" icon={<TeamOutlined />}>
                    <Link to="/customers">Customers</Link>
                  </Menu.Item>
                )}
              </Menu.SubMenu>
            )}

            {(can("location_mgmt") || can("picking_prekey") || can("delivery_planner") || can("records_center") || can("shortages") || isAdmin) && (
              <Menu.SubMenu key="warehouse" icon={<ToolOutlined />} title="Warehouse">
                {can("location_mgmt") && (
                  <Menu.Item key="locations" icon={<EnvironmentOutlined style={{ color: "#0f766e" }} />}>
                    <Link to="/locations">Location Management</Link>
                  </Menu.Item>
                )}
                {can("picking_prekey") && (
                  <Menu.Item key="picking-import" icon={<FileTextOutlined style={{ color: "#16a34a" }} />}>
                    <Link to="/picking/import">Pre-key Draft</Link>
                  </Menu.Item>
                )}
                {can("delivery_planner") && (
                  <Menu.Item key="delivery-planner" icon={<CarOutlined style={{ color: "#2563eb" }} />}>
                    <Link to="/delivery/planner">Delivery Planner</Link>
                  </Menu.Item>
                )}
                {can("shortages") && (
                  <Menu.Item key="shortages" icon={<AlertOutlined style={{ color: "#d97706" }} />}>
                    <Link to="/shortages">Shortages</Link>
                  </Menu.Item>
                )}
                {/* Picking Drafts — hidden */}
                {/* Warehouse Map — hidden */}
              </Menu.SubMenu>
            )}

            {(can("mobile_picking") || can("mobile_quickpull") || can("mobile_outbound") ||
              can("mobile_history") || can("mobile_find") || can("mobile_move") ||
              can("mobile_addstock") || can("mobile_return") ||
              can("mobile_stocktake") || role === "warehouse" || role === "admin") && (
              <Menu.Item key="mobile-hub" icon={<MobileOutlined style={{ color: "#f59e0b" }} />}>
                <Link to="/warehouse/mobile">Mobile App</Link>
              </Menu.Item>
            )}

            {can("records_center") && (
              <Menu.Item key="records" icon={<HistoryOutlined style={{ color: "#0891b2" }} />}>
                <Link to="/records">Records Center</Link>
              </Menu.Item>
            )}

            {role === "admin" && (
              <Menu.Item key="admin" icon={<UserOutlined />}>
                <Link to="/admin">Admin</Link>
              </Menu.Item>
            )}
          </Menu>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOBILE SIDEBAR HOOK
// ─────────────────────────────────────────────────────────────────────────────
function useMobileSidebar() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);
  const [sideOpen, setSideOpen] = useState(false);

  useEffect(() => {
    if (isMobile && sideOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [isMobile, sideOpen]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 991px)");
    const handler = (e) => {
      setIsMobile(e.matches);
      if (!e.matches) setSideOpen(false);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return { isMobile, sideOpen, setSideOpen };
}

function MobileViewportManager() {
  const location = useLocation();
  useEffect(() => {
    const tag = document.querySelector("meta[name=viewport]");
    if (!tag) return;
    if (location.pathname.startsWith("/warehouse/mobile") ||
        location.pathname.startsWith("/warehouse/scan-confirm")) {
      tag.setAttribute("content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no");
    } else {
      tag.setAttribute("content", "width=device-width, initial-scale=1");
    }
  }, [location.pathname]);
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
//  SESSION RESTORE
//
//  SECURITY NOTE (Fix #3 — localStorage privilege escalation)
//  ─────────────────────────────────────────────────────────
//  localStorage data (role, permissions) is used ONLY to drive UI rendering:
//  show/hide menu items, route guards, permission-based component visibility.
//  It is NOT a security boundary — any user can edit it in DevTools.
//
//  The REAL security enforcement is entirely server-side:
//    • requireAuth   — validates JWT signature on every API request
//    • requireRole   — checks role from the JWT payload (req.authUser.role)
//    • permission checks inside route handlers use req.authUser, never client data
//
//  Tampering with localStorage only changes what the user can SEE in the UI.
//  Every write operation will still be rejected by the backend if the JWT
//  does not carry the required role/permission.
// ─────────────────────────────────────────────────────────────────────────────
function restoreSession() {
  try {
    const savedUser  = localStorage.getItem("user");
    const savedToken = localStorage.getItem("authToken");
    if (savedUser && savedToken) {
      return { user: JSON.parse(savedUser), needLogin: false };
    }
  } catch {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
  }
  return { user: null, needLogin: true };
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOBILE HOME PAGE
//  Full-screen nav menu shown as the home page on mobile/tablet devices.
//  Employee taps the section they need → navigates directly.
// ─────────────────────────────────────────────────────────────────────────────
function MobileHomePage({ user, onLogout }) {
  const navigate = useNavigate();
  const can = (perm) => hasPerm(user, perm);
  const role = user?.role || "staff";

  const sections = [
    {
      key: "mobile",
      icon: <MobileOutlined />,
      label: "Mobile App",
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.1)",
      desc: "Quick Pull · Picking · Outbound",
      action: () => navigate("/warehouse/mobile"),
      show: can("mobile_picking") || can("mobile_quickpull") || can("mobile_outbound") ||
            can("mobile_history") || can("mobile_find") || can("mobile_move") ||
            can("mobile_addstock") || can("mobile_return"),
    },
    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
      color: "#2563a8",
      bg: "rgba(37,99,168,0.1)",
      desc: "Overview · Stats · Alerts",
      action: () => navigate("/"),
      show: true,
      isHome: true,
    },
    {
      key: "inventory",
      icon: <FolderOpenOutlined />,
      label: "Inventory",
      color: "#0d9488",
      bg: "rgba(13,148,136,0.1)",
      desc: "Lookup · Analytics · PO · Receiving · SKU Master",
      action: () => navigate("/inventory/lookup"),
      show: can("inventory_ledger") || can("stock_lookup") || can("analytics") ||
            can("purchase_orders") || can("receiving") || can("sku_master"),
    },
    {
      key: "sales",
      icon: <UnorderedListOutlined />,
      label: "Sales & Orders",
      color: "#7c3aed",
      bg: "rgba(124,58,237,0.1)",
      desc: "Sales Orders · Pending · Customers",
      action: () => navigate("/sales/orders"),
      show: can("sales_orders") || can("pending_orders") || can("customers"),
    },
    {
      key: "warehouse",
      icon: <ToolOutlined />,
      label: "Warehouse",
      color: "#15803d",
      bg: "rgba(21,128,61,0.1)",
      desc: "Location Management",
      action: () => navigate("/locations"),
      show: can("location_mgmt"),
    },
    {
      key: "admin",
      icon: <UserOutlined />,
      label: "Admin",
      color: "#dc2626",
      bg: "rgba(220,38,38,0.1)",
      desc: "Users · Roles · System Settings",
      action: () => navigate("/admin"),
      show: role === "admin",
    },
  ].filter(s => s.show && !s.isHome);

  return (
    <div style={{
      minHeight: "100dvh",
      background: "#0f1f32",
      display: "flex",
      flexDirection: "column",
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        padding: "max(20px, env(safe-area-inset-top, 20px)) 20px 16px",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            fontSize: 12, background: "rgba(255,255,255,0.15)", borderRadius: 4,
            padding: "3px 8px", fontWeight: 800, letterSpacing: 2,
            fontFamily: "monospace", color: "#fff",
          }}>WMS</span>
          <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 14 }}>Warehouse</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            background: "#1d4ed8", borderRadius: "50%",
            width: 32, height: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700, color: "#fff",
            border: "1.5px solid rgba(255,255,255,0.2)",
          }}>
            {user?.username?.charAt(0).toUpperCase()}
          </div>
          <button onClick={onLogout} style={{
            background: "none", border: "none", color: "rgba(255,255,255,0.45)",
            cursor: "pointer", fontSize: 13, padding: "4px 8px",
            borderRadius: 6,
          }}>
            Logout
          </button>
        </div>
      </div>

      {/* Welcome */}
      <div style={{ padding: "20px 20px 8px" }}>
        <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, marginBottom: 4 }}>
          Welcome back,
        </div>
        <div style={{ color: "#fff", fontSize: 20, fontWeight: 800, letterSpacing: "-0.3px" }}>
          {user?.username}
          <span style={{
            marginLeft: 8, fontSize: 11, fontWeight: 600,
            color: role === "admin" ? "#f59e0b" : "#7eb3e8",
            background: role === "admin" ? "rgba(245,158,11,0.15)" : "rgba(126,179,232,0.15)",
            border: `1px solid ${role === "admin" ? "rgba(245,158,11,0.3)" : "rgba(126,179,232,0.3)"}`,
            borderRadius: 20, padding: "2px 8px", verticalAlign: "middle",
          }}>
            {role}
          </span>
        </div>
      </div>

      {/* Nav items */}
      <div style={{
        flex: 1, overflowY: "auto", padding: "12px 16px",
        paddingBottom: "max(24px, env(safe-area-inset-bottom, 24px))",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        {sections.map(s => (
          <button
            key={s.key}
            onClick={s.action}
            style={{
              width: "100%", background: "#162840",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 14, padding: "16px 18px",
              cursor: "pointer", textAlign: "left",
              display: "flex", alignItems: "center", gap: 14,
              transition: "background 0.15s",
              WebkitTapHighlightColor: "transparent",
            }}
            onTouchStart={e => e.currentTarget.style.background = "#1e3a5f"}
            onTouchEnd={e => e.currentTarget.style.background = "#162840"}
          >
            {/* Icon circle */}
            <div style={{
              width: 46, height: 46, borderRadius: 12,
              background: s.bg,
              border: `1.5px solid ${s.color}33`,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              fontSize: 20, color: s.color,
            }}>
              {s.icon}
            </div>
            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 16, marginBottom: 2 }}>
                {s.label}
              </div>
              <div style={{ color: "rgba(255,255,255,0.38)", fontSize: 12, whiteSpace: "nowrap",
                overflow: "hidden", textOverflow: "ellipsis" }}>
                {s.desc}
              </div>
            </div>
            {/* Arrow */}
            <div style={{ color: "rgba(255,255,255,0.25)", fontSize: 18, flexShrink: 0 }}>›</div>
          </button>
        ))}

        {/* Switch to Desktop View */}
        <button
          onClick={() => {
            localStorage.setItem("desktopOverride", "1");
            window.location.href = "/";
          }}
          style={{
            width: "100%", background: "transparent",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 14, padding: "14px 18px",
            cursor: "pointer", textAlign: "left",
            display: "flex", alignItems: "center", gap: 14,
            marginTop: 4,
            WebkitTapHighlightColor: "transparent",
          }}
          onTouchStart={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
          onTouchEnd={e => e.currentTarget.style.background = "transparent"}
        >
          <DashboardOutlined style={{ color: "rgba(255,255,255,0.35)", fontSize: 18 }} />
          <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 14 }}>Switch to Desktop View</span>
          <span style={{ marginLeft: "auto", color: "rgba(255,255,255,0.2)", fontSize: 16 }}>›</span>
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _init                     = React.useMemo(() => restoreSession(), []);
  const [loginOpen, setLoginOpen] = useState(_init.needLogin);
  const [user, setUser]           = useState(_init.user);
  const [loading, setLoading]     = useState(false);
  const { isMobile, sideOpen, setSideOpen } = useMobileSidebar();
  // Check if user explicitly requested desktop view
  const [desktopOverride, setDesktopOverride] = useState(
    () => !!localStorage.getItem("desktopOverride")
  );

  useEffect(() => {
    const handleAuthLogout = () => {
      setUser(null);
      setLoginOpen(true);
    };
    window.addEventListener("auth:logout", handleAuthLogout);
    return () => window.removeEventListener("auth:logout", handleAuthLogout);
  }, []);

  const handleLogin = async ({ username, password }) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { username, password });
      const { token, username: uname, role, permissions = [] } = res.data;
      localStorage.setItem("authToken", token);
      // SECURITY: role/permissions stored here are for UI rendering only.
      // All backend routes re-validate identity from the JWT — not from this data.
      const userData = { username: uname, role, permissions };
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
      setLoginOpen(false);
      message.success(`Welcome, ${uname}.`);
      // On mobile: clear desktop override so they see the mobile home page
      if (isMobile) {
        localStorage.removeItem("desktopOverride");
        setDesktopOverride(false);
      }
    } catch (err) {
      message.error(err?.response?.data?.error || "Login error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setLoginOpen(true);
    setDesktopOverride(false);
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    localStorage.removeItem("desktopOverride");
    message.info("Logged out. Please sign in again.");
  };

  const role = user?.role || "staff";

  const can = {
    view:             true,
    createOrder:      role === "admin" || role === "staff",
    delete:           role === "admin",
    editAssemble:     role === "admin",
    confirmOutbound:  role === "admin" || role === "staff",
    accessAdminPage:  role === "admin",
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary:       "#2563a8",
          colorLink:          "#2563a8",
          borderRadius:        6,
          fontFamily:         "Inter, Segoe UI, Helvetica Neue, Arial, sans-serif",
          colorBgLayout:      "#f4f6f9",
          colorBgContainer:   "#ffffff",
          colorBorder:        "#d1d9e3",
          colorTextBase:      "#0f1f32",
          colorTextSecondary: "#4b5d73",
        },
        components: {
          Menu: { darkItemBg: "#0f1f32", darkSubMenuItemBg: "#162840", darkItemSelectedBg: "#2563a8" },
          Table: { headerBg: "#eef1f6", headerColor: "#4b5d73" },
        },
      }}
    >
      <Router>
        <MobileViewportManager />
        <Routes>

          {/* ── Mobile routes — no sidebar, lazy loaded ── */}
          <Route path="/warehouse/mobile" element={
            <Suspense fallback={<PageLoader />}><MobileScan /></Suspense>
          } />
          <Route path="/warehouse/scan-confirm/:invoice_no" element={
            <Suspense fallback={<PageLoader />}>
              {user
                ? <MobileOutboundConfirm username={user?.username} />
                : <MobileScan />}
            </Suspense>
          } />
          <Route path="/warehouse/mobile/picking" element={
            <Suspense fallback={<PageLoader />}>
              {user ? <PickingSession /> : <MobileScan />}
            </Suspense>
          } />
          <Route path="/warehouse/mobile/quickpull" element={
            <Suspense fallback={<PageLoader />}>
              {user ? <MobileQuickPull /> : <MobileScan />}
            </Suspense>
          } />
          <Route path="/warehouse/mobile/history" element={
            <Suspense fallback={<PageLoader />}>
              {user ? <MobilePickingHistory /> : <MobileScan />}
            </Suspense>
          } />

          {/* ── Main layout ── */}
          <Route path="/*" element={
            <MainLayout
              isMobile={isMobile}
              desktopOverride={desktopOverride}
              sideOpen={sideOpen}
              setSideOpen={setSideOpen}
              user={user}
              role={role}
              loginOpen={loginOpen}
              setLoginOpen={setLoginOpen}
              handleLogout={handleLogout}
              handleLogin={handleLogin}
              loading={loading}
              can={can}
            />
          } />

        </Routes>
      </Router>
    </ConfigProvider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN LAYOUT — extracted so useLocation() works inside Router context
// ─────────────────────────────────────────────────────────────────────────────
function MainLayout({
  isMobile, desktopOverride, sideOpen, setSideOpen,
  user, role, loginOpen, setLoginOpen,
  handleLogout, handleLogin, loading, can,
}) {
  const location = useLocation();
  const isMobileHome = isMobile && !desktopOverride && location.pathname === "/";

  // Full-screen mobile home page — no header, no sidebar
  if (isMobileHome && user) {
    return (
      <>
        <MobileHomePage user={user} onLogout={handleLogout} />
        <LoginModal
          open={loginOpen}
          onCancel={user ? () => setLoginOpen(false) : undefined}
          onLogin={handleLogin}
          loading={loading}
        />
      </>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <AppSider
        role={role}
        user={user}
        isMobile={isMobile}
        mobileOpen={isMobile ? sideOpen : true}
        onMobileClose={() => setSideOpen(false)}
      />

      <Layout style={{ marginLeft: isMobile ? 0 : 200, transition: "margin 0.2s" }}>
        <Header style={{
          background: "#fff",
          padding: isMobile ? "0 12px" : "0 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          zIndex: 10,
          gap: 8,
          position: "sticky",
          top: 0,
        }}>
          {isMobile && (
            <Button
              type="text"
              onClick={() => setSideOpen(v => !v)}
              style={{ fontSize: 20, padding: "0 6px", flexShrink: 0 }}
            >
              ☰
            </Button>
          )}
          <div style={{ color: "#888", fontSize: isMobile ? 11 : 13, whiteSpace: "nowrap", flexShrink: 0 }}>
            {isMobile ? "WMS" : "Pius Kitchen & Bath — WMS"}
          </div>
          {user && !isMobile && (
            <div style={{ flex: 1, maxWidth: 360, margin: "0 16px" }}>
              <GlobalSearch />
            </div>
          )}
          <Space align="center" size={isMobile ? 4 : 8} style={{ marginLeft: "auto" }}>
            {user && <NotificationBell />}
            {user ? (
              <>
                {!isMobile && (
                  <Typography.Text style={{ fontSize: 13 }}>
                    <UserOutlined style={{ marginRight: 4 }} />
                    <strong>{user.username}</strong>
                    <span style={{ marginLeft: 6, color: role === "admin" ? "#722ed1" : "#1677ff", fontWeight: 500 }}>
                      ({role})
                    </span>
                  </Typography.Text>
                )}
                <Button size="small" onClick={handleLogout}>
                  {isMobile ? <UserOutlined /> : "Logout"}
                </Button>
              </>
            ) : (
              <Button type="primary" size="small" onClick={() => setLoginOpen(true)}>
                Login
              </Button>
            )}
          </Space>
        </Header>

        <Content style={{ margin: isMobile ? 8 : 16, overflowX: "hidden" }}>
          {!user ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh", flexDirection: "column", gap: 16 }}>
              <span style={{ fontSize: 16, color: "#4b5d73" }}>Please log in to continue.</span>
              <Button type="primary" onClick={() => setLoginOpen(true)}>Login</Button>
            </div>
          ) : (
            /* All routes wrapped in ONE Suspense — shows spinner while any chunk loads */
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Dashboard />} />

                {/* Inventory */}
                <Route path="/inventory/ledger" element={
                  <RequireRole user={user} perm="inventory_ledger">
                    <InventoryLedger />
                  </RequireRole>
                } />
                <Route path="/inventory/lookup" element={
                  <RequireRole user={user} perm="stock_lookup">
                    <InventoryLookup />
                  </RequireRole>
                } />
                <Route path="/analytics" element={
                  <RequireRole user={user} perm="analytics">
                    <InventoryAnalytics />
                  </RequireRole>
                } />
                <Route path="/inventory/purchase-orders" element={
                  <RequireRole user={user} perm="purchase_orders">
                    <PurchaseOrders />
                  </RequireRole>
                } />
                <Route path="/inventory/receiving" element={
                  <RequireRole user={user} perm="receiving">
                    <Receiving />
                  </RequireRole>
                } />
                <Route path="/inventory/sku-master" element={
                  <RequireRole user={user} perm="sku_master">
                    <SkuMaster />
                  </RequireRole>
                } />

                {/* Sales */}
                <Route path="/sales/orders" element={
                  <RequireRole user={user} perm="sales_orders">
                    <SalesOrders can={can} username={user?.username} />
                  </RequireRole>
                } />
                <Route path="/sales/pending" element={
                  <RequireRole user={user} perm="pending_orders">
                    <PendingOrders can={can} username={user?.username} />
                  </RequireRole>
                } />
                <Route path="/sales/out" element={
                  <RequireRole user={user} perm="out_records">
                    <SalesOutRecords can={can} username={user?.username} />
                  </RequireRole>
                } />
                <Route path="/customers" element={
                  <RequireRole user={user} perm="customers">
                    <CustomerOrders username={user?.username} />
                  </RequireRole>
                } />

                {/* Warehouse */}
                {/* /assembly — route kept for deep links, no menu item */}
                <Route path="/assembly" element={
                  <RequireRole user={user} perm="assembly_schedule">
                    <AssemblySchedule can={can} username={user?.username} />
                  </RequireRole>
                } />
                {/* /assembly-board removed from menu; route removed */}
                {/* /management (Operations Centre) removed from menu; route removed */}
                <Route path="/delivery-note" element={
                  <RequireRole user={user} perm="delivery_note">
                    <DeliveryNote />
                  </RequireRole>
                } />
                <Route path="/delivery-note/:invoice_no" element={
                  <RequireRole user={user} perm="delivery_note">
                    <DeliveryNote />
                  </RequireRole>
                } />
                <Route path="/locations" element={
                  <RequireRole user={user} perm="location_mgmt">
                    <LocationManagement />
                  </RequireRole>
                } />
                <Route path="/picking-history" element={
                  <RequireRole user={user} perm="picking_history">
                    <PickingHistoryPage />
                  </RequireRole>
                } />
                {/* /picking/drafts — hidden (route disabled) */}
                <Route path="/picking/import" element={
                  <RequireRole user={user} perm="picking_prekey">
                    <PickingDraftImport user={user} />
                  </RequireRole>
                } />
                <Route path="/delivery/planner" element={
                  <RequireRole user={user} perm="delivery_planner">
                    <DeliveryPlanner user={user} />
                  </RequireRole>
                } />
                <Route path="/shortages" element={
                  <RequireRole user={user} perm="shortages">
                    <Shortages user={user} />
                  </RequireRole>
                } />
                {/* /warehouse/map — hidden (route disabled) */}
                <Route path="/records" element={
                  <RequireRole user={user} perm="records_center">
                    <Records user={user} />
                  </RequireRole>
                } />
                <Route path="/task-preview" element={
                  <RequireRole user={user} perm="location_mgmt">
                    <TaskSheetPreview />
                  </RequireRole>
                } />

                {/* Admin */}
                <Route path="/admin" element={
                  <RequireRole user={user} allow={["admin"]}>
                    <Admin />
                  </RequireRole>
                } />
              </Routes>
            </Suspense>
          )}
        </Content>

        <LoginModal
          open={loginOpen}
          onCancel={user ? () => setLoginOpen(false) : undefined}
          onLogin={handleLogin}
          loading={loading}
        />
      </Layout>
    </Layout>
  );
}

export default App;
